/**
 * The catalog of signs and forms (signs.ts). Every row is one line of docs/source/research/signs-and-forms-utah.md: the URL it was read at, a
 * short quote, how sure we are (MEASURED = the page or text was read on lastChecked; INFERRED = not), and the date. Utah state rule first, then county:
 * a row with `departments` shows only to those health departments. A row that is not required says so (klass "recommended"). We link every official
 * document and rehost none: no page we read gave us leave to copy, and a government sign carries its own logo.
 * Generated from the research table by hand-checked script; edit the research file and this together.
 */
import type { SignDef } from "./signs.ts";
import { SIGNS_CATALOG_UT } from "./states/ut/signs-catalog.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { cite, wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof SIGNS_CATALOG_UT>> = perStateTable("SIGNS_CATALOG", SIGNS_CATALOG_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

const CHECKED = "2026-09-30" as const;


/** The Food Code rows, which every state shares; the state parts (adoption cite, county cite, county sign) come from the state words `w`. */
const GENERIC: Record<string, (w: StateWords<typeof SIGNS_CATALOG_UT>) => SignDef> = {
  "allergen-notice": (w) => (
  {
  id: "allergen-notice", kind: "food-code", klass: "law",
  title: { en: "Allergen notice for your customers", es: "Aviso de alérgenos para sus clientes" },
  citation: `Food Code 3-602.12(C) (2022)${w.adoptedBy ? `, ${w.adoptedBy}` : ""}; see also 2-102.11(C)(9) and 2-103.11(O)`,
  source: { url: "https://www.fda.gov/media/164194/download", quote: "The PERMIT HOLDER shall notify CONSUMERS by written notification of the presence of MAJOR FOOD ALLERGENS as an ingredient in unPACKAGED FOOD items", evidence: "MEASURED", lastChecked: CHECKED },
  permits: ["food_establishment", "temporary_event", "private_members_only", "mobile_food_business"],
  rule: "allergens",
  trigger: "Any unpackaged food you serve that has one of the nine major allergens in it.",
  official: [], rehost: false,
  rehostNote: "Linked, not copied. No page we read gave us leave to host a copy, and a government sign carries its own logo.",
  printable: "allergen-list",
  where: "By the register or on the menu",
  note: "No official Spanish or English county sign was found for this rule. The Food Code asks for written notice; our list names the allergens your food has. Packaged food you sell must carry its own label (FALCPA, not checked here).",
  }
  ),
  "consumer-advisory": (w) => (
  {
  id: "consumer-advisory", kind: "food-code", klass: "law",
  title: { en: "Consumer advisory for raw or undercooked food", es: "Aviso al consumidor sobre comida cruda o poco cocida" },
  citation: cite(`Food Code 3-603.11 and 2-103.11(K)${w.incorporatedBy ? `, ${w.incorporatedBy}` : ""}`, w.consumerAdvisoryCounty),
  source: { url: "https://www.fda.gov/media/164194/download", quote: "the PERMIT HOLDER shall inform CONSUMERS of the significantly increased RISK of consuming such FOODS by way of a DISCLOSURE and REMINDER", evidence: "MEASURED", lastChecked: CHECKED },
  permits: ["food_establishment", "temporary_event", "private_members_only", "mobile_food_business"],
  rule: "advisory",
  trigger: "Your menu has food that can be served raw or undercooked.",
  official: [], rehost: false,
  rehostNote: "Linked, not copied. No page we read gave us leave to host a copy, and a government sign carries its own logo.",
  printable: "advisory",
  where: "On the menu, with an asterisk on each item",
  note: "A posted sign is one allowed way; a menu footnote also works. No official Spanish text exists.",
  }
  ),
  "handwashing-sign": (w) => (
  {
  id: "handwashing-sign", kind: "food-code", klass: "law",
  title: { en: "Handwashing sign at every handwashing sink", es: "Letrero de lavado de manos en cada lavabo de manos" },
  citation: cite(`Food Code 6-301.14 (2022)${w.incorporatedByAdmin ? `, ${w.incorporatedByAdmin}` : ""}`, w.handwashingCounty),
  source: { url: "https://www.fda.gov/media/164194/download", quote: "A sign or poster that notifies food employees to wash their hands shall be provided at all handwashing sinks used by food employees", evidence: "MEASURED", lastChecked: CHECKED },
  permits: ["food_establishment", "temporary_event", "private_members_only"],
  rule: "always",
  trigger: "Every handwashing sink used by food workers needs a sign or poster that tells them to wash.",
  why: "Every handwashing sink used by food workers needs a sign or poster that tells them to wash.",
  official: w.handwashingOfficial ?? [], rehost: false,
  rehostNote: "Linked, not copied. No page we read gave us leave to host a copy, and a government sign carries its own logo.",
  printable: "handwashing",
  where: "At each handwashing sink",
  }
  ),
  "food-permit": (w) => (
  {
  id: "food-permit", kind: "license", klass: "law",
  title: { en: "Food establishment permit", es: "Permiso del establecimiento de alimentos" },
  citation: `Food Code 8-304.11(A) (2022)${w.incorporatedBy ? `, ${w.incorporatedBy}` : ""}`,
  source: { url: "https://www.fda.gov/media/164194/download", quote: "Post the PERMIT in a location in the FOOD ESTABLISHMENT that is conspicuous to CONSUMERS", evidence: "MEASURED", lastChecked: CHECKED },
  permits: ["food_establishment", "temporary_event", "private_members_only"],
  rule: "always",
  trigger: "Your health permit hangs where customers can see it.",
  why: "Your health permit hangs where customers can see it.",
  official: [], rehost: false,
  rehostNote: "Linked, not copied. No page we read gave us leave to host a copy, and a government sign carries its own logo.",
  where: "Near the entrance, where customers can see it",
  note: "Issued by your health department. We link no copy: yours is the one with your name on it.",
  expires: "permit",
  }
  ),
  "employee-health-agreement": (w) => (
  {
  id: "employee-health-agreement", kind: "food-code", klass: "law",
  title: { en: "Employee health agreement", es: "Acuerdo de salud del empleado" },
  citation: "Food Code 2-103.11(P) and 2-201.11 (2022); form: Food Code Annex 7 Form 1-B. The Code requires informing employees 'in a verifiable manner'; the specific form is a model, not mandatory",
  source: { url: "https://www.fda.gov/media/164194/download", quote: "FOOD EMPLOYEES and CONDITIONAL EMPLOYEES are informed in a verifiable manner of their responsibility to report", evidence: "MEASURED", lastChecked: CHECKED },
  permits: ["food_establishment", "temporary_event", "private_members_only", "mobile_food_business"],
  rule: "always",
  trigger: "Every food worker, new hires included, is told in a way you can verify to report illness. The FDA Form 1-B is one way.",
  why: "Every food worker, new hires included, is told in a way you can verify to report illness. The FDA Form 1-B is one way.",
  official: [{ lang: "both", label: "FDA Form 1-B (English and Spanish)", url: "https://seuhealth.gov/wp-content/uploads/2025/04/Employee-Sick-Policy-Food-Establishment_English-Spanish_042423-3.pdf" }], rehost: false,
  rehostNote: "Linked, not copied. No page we read gave us leave to host a copy, and a government sign carries its own logo.",
  where: "In each worker's file",
  note: "The form is a model, not mandatory. The daily check-in in this app also tells each person what to report.",
  }
  ),
  "clean-tableware-notice": (w) => (
  {
  id: "clean-tableware-notice", kind: "food-code", klass: "law",
  title: { en: "Clean plate for each trip to the buffet", es: "Plato limpio para cada visita al buffet" },
  citation: `Food Code 2-103.11(M) and 3-304.16${w.incorporatedBy ? `, ${w.incorporatedBy}` : ""}`,
  source: { url: "https://www.fda.gov/media/164194/download", quote: "CONSUMERS are notified that clean TABLEWARE is to be used when they return to self-service areas such as salad bars and buffets", evidence: "MEASURED", lastChecked: CHECKED },
  permits: ["food_establishment", "temporary_event", "private_members_only", "mobile_food_business"],
  rule: "buffet",
  trigger: "Customers must be told to take a clean plate when they come back to the buffet or salad bar.",
  why: "Customers must be told to take a clean plate when they come back to the buffet or salad bar.",
  official: [], rehost: false,
  rehostNote: "Linked, not copied. No page we read gave us leave to host a copy, and a government sign carries its own logo.",
  where: "At the start of the buffet line",
  note: "No official sign or Spanish text found.",
  }
  ),
};

/** Catalog order (every row of every state, in the order shown). A row a state has no words for is left out. */
const ORDER: readonly string[] = ["nv-snhd-grade-card", "allergen-notice", "seuhd-allergen-sign", "consumer-advisory", "handwashing-sign", "mfb-handwashing-sign", "food-permit", "ca-prop65-food", "ca-prop65-alcohol", "mfb-permit", "slco-permit-visible-outside", "summit-permit-posted", "tooele-permit-posted", "weber-temp-permit-posted", "food-handler-permits", "mfb-staff-proof", "cfsm-certificate", "weber-cfsm-posted", "slco-cfsm-form", "employee-health-agreement", "clean-tableware-notice", "mfb-commissary-agreement", "emp-workers-comp", "emp-uosh", "emp-flsa", "emp-eeo", "emp-pregnancy", "emp-fmla", "emp-ui-notice", "emp-userra", "emp-eppa"];

/** The signs and forms catalog for a state (absent = Utah): the Food Code rows plus that state's own rows, in catalog order. */
/** Rows only another state has (Nevada's), after Utah's order. */
const ORDER_TAIL: readonly string[] = ["nv-pregnancy-alcohol-warning", "inspection-report-available"];

export function signsCatalogFor(state?: UsState): readonly SignDef[] {
  const w = words(state);
  const rows: SignDef[] = [];
  for (const id of [...ORDER, ...ORDER_TAIL]) {
    const g = GENERIC[id];
    const over = g ? (w.generic as Record<string, Partial<SignDef> | null> | null)?.[id] : undefined;
    if (g && over === null) continue;
    const row = g ? { ...g(w), ...(over ?? {}) } : ((w.rows ?? {}) as Record<string, SignDef | undefined>)[id];
    if (row) rows.push(row);
  }
  return rows;
}

export const SIGN_CATALOG: readonly SignDef[] = signsCatalogFor("UT");
